import { createFileRoute } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Pre, Automate, H3 } from '../components/docs/prose'

export const Route = createFileRoute('/docs/investments')({ component: Investments })

const SECTIONS = [
  { id: 'why', title: 'Why ACB exists' },
  { id: 'pooling', title: 'Pooling: the rule that trips people up' },
  { id: 'lots', title: 'Lots' },
  { id: 'selling', title: 'What happens when you sell' },
  { id: 'splits', title: 'Splits and transfers' },
  { id: 'dividends', title: 'Dividends and return of capital' },
  { id: 'limits', title: 'What it does not do' },
]

function Investments() {
  return (
    <DocPage
      path="/docs/investments"
      title="Investments and ACB"
      lede={
        <>
          Adjusted cost base is the number CRA wants when you sell something
          outside a registered account. FolioWorth tracks it per lot, so it is
          a record rather than a reconstruction every April.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="why" title="Why ACB exists">
        <P>
          When you sell a security in a non-registered account, the taxable
          capital gain is the proceeds minus your adjusted cost base. ACB is
          what you paid, adjusted for everything that happened since —
          reinvested distributions, return of capital, splits, additional
          purchases at different prices.
        </P>
        <P>
          It is a running number, and the work of maintaining it is the whole
          problem. Buy the same ETF nine times over four years and your cost
          base is nine purchases deep. Sell some, and you have to know what
          portion of that base went with them. People do this in a spreadsheet
          once a year, from statements, and it is where errors live.
        </P>
        <P>
          Inside an RRSP, TFSA or RESP none of this matters — those are
          sheltered, and there is no capital gain to report. ACB is a
          non-registered concern.
        </P>
      </Section>

      <Section id="pooling" title="Pooling: the rule that trips people up">
        <P>
          CRA pools <strong>identical property per taxpayer</strong>, not per
          account. If you hold BCE.TO in a Wealthsimple non-registered account
          and in a TD margin account, that is <strong>one</strong> pool with one
          average cost — and a sale from either uses that average. Computing ACB
          per account produces a different, wrong number for every disposition,
          and the error compounds silently across years.
        </P>
        <P>
          FolioWorth pools across every one of your non-registered accounts,
          whichever institution holds them. Three rules follow from that, and
          all three are enforced rather than assumed:
        </P>
        <DefList
          items={[
            [
              'Registered accounts are excluded',
              'RRSP, TFSA, RESP, LIRA, RRIF and FHSA holdings have no capital-gains treatment, so they have no ACB to track. Pooling them with taxable holdings would corrupt the taxable average with cost that can never be disposed of taxably.',
            ],
            [
              'Spouses are separate',
              'Two people each holding BCE.TO are two taxpayers with two pools. Merging them at the household level is the same error one level up — and it is invisible in the resulting number.',
            ],
            [
              'Unknown stays out',
              'An account whose treatment cannot be inferred is excluded from pooling and surfaced for you to set. Guessing either way changes a tax figure, and neither direction is a safe default.',
            ],
          ]}
        />
        <P>
          Set an account&rsquo;s treatment on the account itself. Names that
          clearly say <C>RRSP</C>, <C>TFSA</C>, <C>Margin</C> or{' '}
          <C>Non-registered</C> are classified automatically; anything
          ambiguous waits for you.
        </P>
        <H3>Tickers are normalised first</H3>
        <P>
          Brokers disagree on symbols: Wealthsimple exports <C>BCE.TO</C>, TD
          exports <C>BCE</C>. Left alone, the same security bought at two
          brokers forms two pools. FolioWorth <em>adds</em> the exchange suffix
          to Canadian listings rather than stripping it — <C>T</C> is Telus on
          the TSX and AT&amp;T on the NYSE, so stripping would merge two
          genuinely different securities and produce a plausible wrong answer,
          which is worse than an obviously missing one.
        </P>
      </Section>

      <Section id="lots" title="Lots">
        <P>
          A <strong>lot</strong> is one acquisition: a symbol, a number of
          shares, a date, a total cost and a cost per share. Every purchase
          creates one. Reinvested distributions and transfers in create one
          too, because they are acquisitions with a cost.
        </P>
        <Pre>{`VFV   40 sh   2023-02-14   $4,120.00   $103.00/sh
VFV   25 sh   2023-09-06   $2,780.00   $111.20/sh
VFV   30 sh   2024-04-19   $3,690.00   $123.00/sh`}</Pre>
        <P>
          Your position is the sum of the lots. The pooled cost base is the sum
          of their costs, and the per-share figure is that total over the total
          shares — for the example above, $10,590 over 95 shares, or $111.47
          per share.
        </P>
      </Section>

      <Section id="selling" title="What happens when you sell">
        <P>
          Lots are consumed oldest first. A sale walks them in acquisition
          order, taking whole lots until the remainder fits inside one, which
          is then reduced in place.
        </P>
        <Pre>{`Sell 50 VFV at $128.00       proceeds   6,400.00

  consumes  40 sh from 2023-02-14   cost  4,120.00
            10 sh from 2023-09-06   cost  1,112.00
                                    ACB   5,232.00

capital gain                              1,168.00`}</Pre>
        <P>
          The result reports the proceeds, the ACB consumed, the gain, and
          which lots were used. Remaining shares stay in their lots with their
          original acquisition dates, which is what you need if you ever have
          to substantiate the calculation.
        </P>
        <H3>A note on method</H3>
        <P>
          CRA&rsquo;s rule for identical properties is a pooled average cost,
          not first-in-first-out. FolioWorth&rsquo;s pooled figure —{' '}
          <C>total_cost_basis</C> over <C>total_shares</C> — is the average you
          want for reporting a position. The disposition walk is FIFO, so for a
          holding bought at several prices the per-sale gain figure can differ
          from the average-cost result. Check a disposition against the pooled
          number before filing on it.
        </P>
      </Section>

      <Section id="splits" title="Splits and transfers">
        <P>
          A stock split is applied as a ratio. Shares multiply, cost per share
          divides, and the total cost base is unchanged — which is the correct
          treatment, because a split is not a transaction and gives you nothing
          you did not already own.
        </P>
        <P>
          Transfers in and out are treated as acquisitions and dispositions
          respectively. Moving a holding between two of your own non-registered
          accounts should carry its cost base with it; check the lots landed
          with the right dates and costs after a transfer, because the
          receiving institution&rsquo;s statement often will not tell you.
        </P>
      </Section>

      <Section id="dividends" title="Dividends and return of capital">
        <P>
          Distributions imported from a brokerage export appear under{' '}
          <strong>Dividends</strong>: income per holding, all-time and over the
          trailing twelve months, with a forward estimate.
        </P>
        <P>
          The estimate is the trailing twelve months carried forward. It does
          not model ex-dividend dates, declared increases or payment schedules,
          because none of that is in the imported data. A position you sold last
          year projects zero rather than repeating history you no longer hold,
          and one bought two months ago understates — both visible from the
          payment count and last-paid date shown beside it.
        </P>
        <P>
          <strong>Return of capital is excluded from income</strong>, and that
          is deliberate. ROC is not a distribution of earnings; it reduces your
          cost base, which increases the eventual capital gain. Counting it as
          income would overstate yield in one direction and understate the gain
          in the other. Cancelled dividends are subtracted rather than ignored.
        </P>
        <P>
          Totals are kept per currency and never summed across them. A US
          dividend added to a Canadian one at face value understates by roughly
          the exchange rate, and nothing on screen would look wrong.
        </P>
      </Section>

      <Section id="limits" title="What it does not do">
        <P>
          Read this section before you file a return against these numbers.
        </P>
        <DefList
          items={[
            [
              'Superficial losses',
              'Not implemented. If you sell at a loss and buy back within thirty days, CRA denies the loss and adds it to the cost base of the repurchase. FolioWorth will report the loss as realised. You must adjust this yourself.',
            ],
            [
              'Commissions and fees',
              'Not included. CRA lets you add the commission to the cost base on a buy and subtract it from proceeds on a sale. FolioWorth records price times shares, so a commission-heavy account will show a slightly overstated gain.',
            ],
            [
              'Foreign currency',
              'Not converted. CRA requires cost base and proceeds in Canadian dollars at the rates on the respective transaction dates. A US-dollar holding is tracked in its own numbers, so the reported gain is not the number that goes on a T1.',
            ],
            [
              'Registered accounts',
              'Nothing distinguishes them here. Cost base is tracked for whatever you enter; it is simply not meaningful for sheltered accounts.',
            ],
          ]}
        />
        <P>
          These are stated rather than glossed because a tax number that is
          quietly approximate is worse than no number at all. What FolioWorth
          gives you is a maintained, per-lot record with the acquisition
          history intact, which is most of the work. The adjustments above are
          the part you or your accountant still make.
        </P>
      </Section>

      <Automate>
        Lots come from investment transactions, and investment transactions can
        arrive from a linked brokerage — Plaid accounts typed{' '}
        <C>investment</C> or <C>brokerage</C> map to FolioWorth&rsquo;s{' '}
        <C>Investment</C> type. What you should never be doing is rebuilding
        four years of purchase history from PDFs in March.
      </Automate>
    </DocPage>
  )
}
